/**
 * Shared Axios client. Mounts the inference-customer auth token from the
 * Zustand auth store on every request, and triggers logout on 401.
 */
import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api.app.nurev.io';

export const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' }
});

client.interceptors.request.use(cfg => {
  const t = useAuthStore.getState().token;
  if (t && cfg.headers) {
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

client.interceptors.response.use(
  r => r,
  (err: AxiosError) => {
    // On 401, drop the session — the auth guard will redirect to /login.
    // Don't fight back; the user will sign in again via magic link.
    if (err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  }
);

/** Unwrap the API's standard `{ success, data }` envelope. */
export async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}
